import { useEffect, useCallback, useRef } from 'react';
import { jukeboxRepository } from '../../data/jukeboxRepository';
import { useFilaCooldown } from './useFilaCooldown';
import type { FilaJukebox } from '../../domain/entities/FilaJukebox';

const ACTIVE_SYNC_MS = 3 * 60 * 1_000;

/**
 * Mantém a fila ativa da tela para o TvVibeTakeover (TV/Player).
 *
 * Reaproveita o CDC de `useFilaCooldown` (dedupe de INSERT, janela por fetch
 * com merge tombstone-safe e catch-up a cada reassinatura). Antes este hook
 * tinha um canal próprio que duplicava itens (INSERT sem dedupe + snapshot
 * cego) e nunca reconciliava — numa TV 24/7 o placar romance × sofrência
 * se afastava do real. Active sync de 3 min, igual ao do Jukebox.
 */
export function useVibeTakeoverFila(
  telaId: string | undefined,
  onNovoVibe?: (vibe: 'romance' | 'sofrencia') => void,
): FilaJukebox[] {
  const onNovoVibeRef = useRef(onNovoVibe);
  useEffect(() => { onNovoVibeRef.current = onNovoVibe; });

  const onInsert = useCallback((item: FilaJukebox) => {
    if (item.vibe === 'romance' || item.vibe === 'sofrencia') onNovoVibeRef.current?.(item.vibe);
  }, []);

  const { filaAtiva, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch } =
    useFilaCooldown(telaId, onInsert);

  useEffect(() => {
    if (!telaId) return;
    const sincronizar = () => {
      const token = iniciarJanelaFetch();
      jukeboxRepository.listarAtivos(telaId)
        .then(fila => aplicarSnapshotFila(fila, token))
        .catch(() => cancelarJanelaFetch(token));
    };
    sincronizar();
    const id = setInterval(sincronizar, ACTIVE_SYNC_MS);
    return () => clearInterval(id);
  }, [telaId, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch]);

  return filaAtiva;
}
